import { useQuery, useQueryClient } from "@tanstack/react-query";
import { EntityTypeFeaturedIcon } from "@/features/entity-types/components/entity-type-featured-icon";
import { placeFormColumn } from "@/features/entity-types/lib/place-form-column";
import {
	createFileRoute,
	Outlet,
	useNavigate,
	useRouterState,
} from "@tanstack/react-router";
import { Copy01, Cube01, Edit05, Expand03, Trash02 } from "@untitledui/icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/base/buttons/button";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { Label } from "@/components/base/label";
import { EmptyValue } from "@/components/common/EmptyValue";
import CopyableId from "@/components/Molecules/CopyableId/CopyableId";
import {
	TableCellContent,
	TableCellContentType,
} from "@/components/Molecules/Table/table-cell-content";
import type { ColumnDef } from "@/components/Molecules/Table";
import { CustomTableWrapper } from "@/components/Molecules/Table/CustomTableWrapper";
import { TableSearchEmptyState } from "@/components/Molecules/Table/TableSearchEmptyState";
import { getActionsColumnHeader } from "@/components/Molecules/Table/table-column-headers";
import { CreateAssetTypeModal } from "@/components/Organisms/CreateAssetTypeModal/CreateAssetTypeModal";
import { DeleteAssetTypeModal } from "@/components/Organisms/DeleteAssetTypeModal/DeleteAssetTypeModal";
import { DuplicatingEntityTypeModal } from "@/components/Organisms/DuplicatingEntityTypeModal/DuplicatingEntityTypeModal";
import { PageHeader } from "@/components/Organisms/PageHeader/PageHeader";
import { PageLayout } from "@/components/Organisms/PageLayout/PageLayout";
import { EMPTY_ORG_ID } from "@/constants/org";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useCanCreateFormLayout } from "@/features/form-editor/hooks/use-can-create-form-layout";
import { useTableSettings } from "@/hooks/use-table-settings";
import {
	useDuplicateAssetType,
	useExportAssetTypes,
} from "@/services/asset/asset.mutations";
import { assetKeys, assetQueryOptions } from "@/services/asset/asset.options";
import type {
	AssetType,
	AssetTypesListParams,
} from "@/services/asset/asset.types";
import { authQueryOptions } from "@/services/auth/auth.options";
import { formatGeometryTypeLabel } from "@/utils/asset-type/asset-geometry-types.utils";
import { downloadBlob, generateExportFilename } from "@/utils/download";
import { resolveIconComponent } from "@/utils/icon-catalog";
import { showApiErrorToast } from "@/utils/toast.utils";

export const Route = createFileRoute(
	"/_authenticated/(admin-app)/_admin/admin/configuration/assetTypes",
)({
	component: RouteComponent,
});

const TABLE_KEY = "admin.configuration.assetTypes";

const DEFAULT_COLUMN_ORDER = [
	"name",
	"description",
	"lifecycle",
	"geometryType",
	"source",
	"form",
	"id",
	"actions",
];
const DEFAULT_SORTING = [{ id: "name", desc: false }];
const DEFAULT_PAGE_SIZE = 10;
const ACTIONS_COLUMN_ID = "actions";

const normalizeColumnOrder = (columnOrder?: string[]) => {
	const nonActionDefaultColumns = DEFAULT_COLUMN_ORDER.filter(
		(columnId) => columnId !== ACTIONS_COLUMN_ID,
	);

	if (!Array.isArray(columnOrder) || columnOrder.length === 0) {
		return [...nonActionDefaultColumns, ACTIONS_COLUMN_ID];
	}

	const allowedColumns = new Set(DEFAULT_COLUMN_ORDER);
	const sanitized = columnOrder.filter(
		(columnId) =>
			allowedColumns.has(columnId) && columnId !== ACTIONS_COLUMN_ID,
	);
	const unique = placeFormColumn(Array.from(new Set(sanitized)));
	const missingDefaults = nonActionDefaultColumns.filter(
		(columnId) => columnId !== "form" && !unique.includes(columnId),
	);

	return [...unique, ...missingDefaults, ACTIONS_COLUMN_ID];
};

const serverSortFieldMapping: Record<string, string[]> = {
	name: ["Name"],
	description: ["Description"],
	id: ["Id"],
};

function resolveAssetTypeIcon(
	icon?: string | null,
	typeName?: string | null,
): typeof Cube01 {
	const raw = (icon ?? typeName ?? "").trim();
	if (!raw) return Cube01;
	return resolveIconComponent(raw) ?? Cube01;
}

function AssetTypeDeleteAction({
	assetType,
	onDelete,
}: {
	assetType: AssetType;
	onDelete: () => void;
}) {
	const { t } = useAppTranslation();
	const canDelete = useQuery({
		...authQueryOptions.checkAction({
			name: "DeleteAssetType",
			entityType: "AssetType",
			entityId: assetType.id,
		}),
	});

	if (!canDelete.data) return null;

	return (
		<>
			<Dropdown.Separator />
			<Dropdown.Item
				data-testid="assettype-table-action-delete"
				id="delete"
				label={t("admin.assetTypes.table.actions.delete")}
				icon={Trash02}
				className="[&_span]:text-red-600"
				onAction={onDelete}
			/>
		</>
	);
}

function AssetTypesTable({
	tableOptionsPortalTarget = null,
}: {
	tableOptionsPortalTarget?: HTMLElement | null;
}) {
	const canEditForm = useCanCreateFormLayout();
	const { t } = useAppTranslation();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [pageIndex, setPageIndex] = useState(0);
	const [searchTerm, setSearchTerm] = useState("");
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [sortParams, setSortParams] = useState({
		"OrderBy.Fields": ["Name"] as string[],
		"OrderBy.Descending": false,
	});
	const [selectedAssetType, setSelectedAssetType] = useState<{
		id: string;
		name: string;
	} | null>(null);

	const hasAppliedPersistedSettings = useRef(false);

	const {
		settings: tableSettings,
		isLoaded: settingsLoaded,
		saveSorting,
		saveColumnOrder,
		saveColumnVisibility,
		saveSearchTerm,
		savePageSize,
	} = useTableSettings<Record<string, never>>({
		tableKey: TABLE_KEY,
		defaultSorting: DEFAULT_SORTING,
		defaultColumnOrder: DEFAULT_COLUMN_ORDER,
		defaultSearchTerm: "",
		defaultFilters: {},
		defaultPageSize: DEFAULT_PAGE_SIZE,
	});

	useEffect(() => {
		if (!settingsLoaded || hasAppliedPersistedSettings.current) return;
		hasAppliedPersistedSettings.current = true;
		if (tableSettings.searchTerm) setSearchTerm(tableSettings.searchTerm);
		if (
			Number.isFinite(tableSettings.pageSize) &&
			[10, 25, 50, 100].includes(tableSettings.pageSize)
		) {
			setPageSize(tableSettings.pageSize);
		}
	}, [settingsLoaded, tableSettings.searchTerm, tableSettings.pageSize]);

	const listParams = useMemo<AssetTypesListParams>(() => ({}), []);

	const searchTrimmed = searchTerm.trim();

	const mergedServerParams = useMemo(
		() => ({
			...listParams,
			...sortParams,
			...(searchTrimmed ? { SearchTerm: searchTrimmed } : {}),
		}),
		[listParams, sortParams, searchTrimmed],
	);

	const apiStart = pageIndex * pageSize + 1;

	const countQuery = useQuery({
		...assetQueryOptions.typesCount(mergedServerParams),
	});

	const unfilteredCountQuery = useQuery({
		...assetQueryOptions.typesCount(listParams),
	});

	const pageQuery = useQuery({
		...assetQueryOptions.typesPage(apiStart, pageSize, mergedServerParams),
	});

	const data = useMemo(() => pageQuery.data ?? [], [pageQuery.data]);

	const pageCount = useMemo(() => {
		const total = countQuery.data ?? 0;
		if (!total) return 0;
		return Math.ceil(total / pageSize);
	}, [countQuery.data, pageSize]);

	const exportMutation = useExportAssetTypes();

	const onSortChange = useCallback(
		({ fields, descending }: { fields: string[]; descending: boolean }) => {
			setSortParams({
				"OrderBy.Fields": fields,
				"OrderBy.Descending": descending,
			});
		},
		[],
	);

	const handleRefresh = async () => {
		setIsRefreshing(true);
		try {
			await queryClient.invalidateQueries({
				queryKey: assetKeys.types(),
			});
		} finally {
			setIsRefreshing(false);
		}
	};

	const columnLabels = useMemo(
		() => ({
			name: t("admin.assetTypes.table.columns.name"),
			description: t("admin.assetTypes.table.columns.description"),
			lifecycle: t("admin.assetTypes.table.columns.lifecycle"),
			geometryType: t("admin.assetTypes.table.columns.geometryType"),
			source: t("admin.assetTypes.table.columns.source"),
			form: t("admin.assetTypes.table.columns.form"),
			id: t("admin.assetTypes.table.columns.id"),
		}),
		[t],
	);

	const handleExport = useCallback(
		(visibleColumns?: string[]) => {
			const totalCount = countQuery.data ?? 0;
			if (totalCount === 0 || totalCount > 10000) return;

			const allIds = DEFAULT_COLUMN_ORDER;
			const ids = (visibleColumns?.length ? visibleColumns : allIds).filter(
				(c) => c !== "actions",
			);

			const apiColumnById: Record<string, string> = {
				name: "Name",
				description: "Description",
				lifecycle: "StatusLifecycleId",
				geometryType: "GeometryType",
				source: "Source",
				id: "Id",
			};
			const apiColumns = ids
				.map((c) => apiColumnById[c])
				.filter(Boolean)
				.join(",");

			exportMutation.mutate(
				{
					startIndex: 1,
					count: totalCount,
					params: {
						...mergedServerParams,
						...(apiColumns ? { columns: apiColumns } : {}),
					},
				},
				{
					onSuccess: (blob) => {
						downloadBlob(blob, generateExportFilename("asset-types"));
					},
				},
			);
		},
		[countQuery.data, exportMutation, mergedServerParams],
	);

	const duplicateAssetTypeMutation = useDuplicateAssetType();
	const tableAnchorRef = useRef<HTMLDivElement>(null);
	const duplicateAssetType = useCallback(
		(assetTypeId: string) => {
			duplicateAssetTypeMutation.mutate(
				{ assetTypeId, organizationId: EMPTY_ORG_ID },
				{
					onSuccess: (result) => {
						if (result.affectedId) {
							void navigate({
								to: "/admin/configuration/assetTypes/$assetTypeId/details",
								params: { assetTypeId: result.affectedId },
								search: { mode: "view", presentation: "drawer" },
							});
						}
					},
					onError: (error) => {
						showApiErrorToast({
							error,
							message: t("admin.assetTypes.modal.createError"),
						});
					},
				},
			);
		},
		[duplicateAssetTypeMutation, navigate, t],
	);

	const handleRowClick = useCallback(
		(row: AssetType) => {
			void navigate({
				to: "/admin/configuration/assetTypes/$assetTypeId/details",
				params: { assetTypeId: row.id },
				search: { mode: "view", presentation: "drawer" },
			});
		},
		[navigate],
	);

	const navigateFullPage = useCallback(
		(assetTypeId: string) => {
			void navigate({
				to: "/admin/configuration/assetTypes/$assetTypeId/full",
				params: { assetTypeId },
				search: {
					tab: "details",
					mode: "view",
					presentation: "full",
				},
			});
		},
		[navigate],
	);

	const columns = useMemo<ColumnDef<AssetType>[]>(() => {
		return [
			{
				id: "name",
				accessorKey: "name",
				header: columnLabels.name,
				enableSorting: true,
				meta: {
					headerProps: { isRowHeader: true },
				},
				minSize: 280,
				cell: ({ row }) => {
					const at = row.original;
					const Icon = resolveAssetTypeIcon(at.icon, at.name);
					return (
						<div className="flex items-center gap-3">
							<EntityTypeFeaturedIcon icon={Icon} />
							<div className="min-w-0">
								{at.name ? (
									<p className="text-sm font-medium text-primary">{at.name}</p>
								) : (
									<EmptyValue />
								)}
							</div>
						</div>
					);
				},
			},
			{
				id: "description",
				accessorKey: "description",
				header: columnLabels.description,
				enableSorting: true,
				minSize: 280,
				cell: ({ row }) => {
					return row.original.description ? (
						<span className="text-sm font-medium text-primary">
							{row.original.description}
						</span>
					) : (
						<EmptyValue />
					);
				},
			},
			{
				id: "lifecycle",
				accessorFn: (row) => row.statusLifecycleName?.trim() || "",
				header: columnLabels.lifecycle,
				enableSorting: false,
				minSize: 200,
				cell: ({ row }) => {
					const label = row.original.statusLifecycleName?.trim();
					return label ? (
						<span className="text-sm font-medium text-primary">{label}</span>
					) : (
						<span className="text-sm text-secondary">
							{t("admin.assetTypes.table.lifecycle.notConfigured")}
						</span>
					);
				},
			},
			{
				id: "geometryType",
				accessorFn: (row) => row.geometryType ?? "",
				header: columnLabels.geometryType,
				enableSorting: false,
				minSize: 160,
				cell: ({ row }) => {
					return row.original.geometryType ? (
						<span className="text-sm font-medium text-primary">
							{formatGeometryTypeLabel(row.original.geometryType)}
						</span>
					) : (
						<EmptyValue />
					);
				},
			},
			{
				id: "source",
				accessorFn: (row) => (row.defaultType ? "system" : "custom"),
				header: columnLabels.source,
				enableSorting: false,
				minSize: 120,
				cell: ({ row }) => (
					<Label size="md" type="dark" showIcon={false}>
						{row.original.defaultType
							? t("admin.assetTypes.table.source.system")
							: t("admin.assetTypes.table.source.custom")}
					</Label>
				),
			},
			{
				id: "form",
				header: columnLabels.form,
				enableSorting: false,
				minSize: 120,
				cell: ({ row }) => (
					<TableCellContent
						type={TableCellContentType.Secondary}
						primary={
							row.original.hasForm === true
								? t("common.actions.yes")
								: t("common.actions.no")
						}
					/>
				),
			},
			{
				id: "id",
				accessorKey: "id",
				header: columnLabels.id,
				enableSorting: true,
				minSize: 200,
				cell: ({ row }) => {
					return row.original.id ? (
						<CopyableId value={row.original.id} />
					) : (
						<EmptyValue />
					);
				},
			},
			{
				id: "actions",
				header: getActionsColumnHeader(t),
				enableSorting: false,
				enableHiding: false,
				size: 56,
				meta: {
					disableColumnReorder: true,
					rowFullViewAction: (row: unknown) =>
						navigateFullPage((row as AssetType).id),
					cellProps: {
						className: "w-14 px-2",
						"data-suppress-row-click": true,
					},
				},
				cell: ({ row }) => {
					const at = row.original;
					return (
						<div className="flex justify-end" data-suppress-row-click>
							<Dropdown.Root>
								<Dropdown.DotsButton
									data-testid="assettype-table-row-actions-btn"
									aria-label={t("admin.assetTypes.table.actionsMenu.ariaLabel")}
								/>
								<Dropdown.Popover className="w-min">
									<Dropdown.Menu>
										<Dropdown.Item
											data-testid="assettype-table-action-view-details"
											id="view"
											label={t("admin.assetTypes.actions.viewFullDetails")}
											icon={Expand03}
											onAction={() => navigateFullPage(at.id)}
										/>
										{canEditForm ? (
											<Dropdown.Item
												data-testid="assettype-table-action-edit-form"
												id="edit"
												label={t("admin.assetTypes.table.actions.editForm")}
												icon={Edit05}
												onAction={() => {
													void navigate({
														to: "/admin/configuration/assetTypes/$assetTypeId/full/form",
														params: { assetTypeId: at.id },
														search: {
															tab: "details",
															mode: "view",
															presentation: "full",
														},
													});
												}}
											/>
										) : null}
										<Dropdown.Item
											data-testid="assettype-table-action-duplicate"
											id="duplicate"
											label={t("admin.assetTypes.table.actions.duplicate")}
											icon={Copy01}
											isDisabled={
												duplicateAssetTypeMutation.isPending &&
												duplicateAssetTypeMutation.variables?.assetTypeId ===
													at.id
											}
											onAction={() => duplicateAssetType(at.id)}
										/>
										<AssetTypeDeleteAction
											assetType={at}
											onDelete={() =>
												setSelectedAssetType({
													id: at.id,
													name: at.name ?? at.id,
												})
											}
										/>
									</Dropdown.Menu>
								</Dropdown.Popover>
							</Dropdown.Root>
						</div>
					);
				},
			},
		];
	}, [
		canEditForm,
		columnLabels,
		duplicateAssetType,
		duplicateAssetTypeMutation.isPending,
		duplicateAssetTypeMutation.variables?.assetTypeId,
		navigateFullPage,
		navigate,
		t,
	]);

	useEffect(() => {
		const total = countQuery.data ?? 0;
		if (!total) return;
		const pages = Math.max(1, Math.ceil(total / pageSize));
		if (pageIndex > 0 && pageIndex >= pages) {
			setPageIndex(Math.max(0, pages - 1));
		}
	}, [countQuery.data, pageSize, pageIndex]);

	const loadError = pageQuery.error ?? countQuery.error;

	if (loadError) {
		return (
			<div className="flex flex-col items-center justify-center py-12">
				<h3 className="text-sm font-medium text-error">
					{t("admin.assetTypes.errors.failedToLoadAssetTypes")}
				</h3>
				<p className="mt-1 text-sm text-tertiary">{loadError.message}</p>
				<Button
					size="sm"
					color="primary"
					onClick={handleRefresh}
					className="mt-3"
				>
					{t("common.actions.tryAgain")}
				</Button>
			</div>
		);
	}

	return (
		<>
			<div ref={tableAnchorRef} className="w-full">
				<CustomTableWrapper<AssetType>
					tableKey={TABLE_KEY}
					data={data}
					columns={columns}
					showHeaderPadding={false}
					zebraStriping
					isLoading={pageQuery.isLoading || countQuery.isLoading}
					isFetching={
						pageQuery.isFetching ||
						countQuery.isFetching ||
						unfilteredCountQuery.isFetching
					}
					isRefreshing={isRefreshing}
					onRefresh={handleRefresh}
					paginationMode="server"
					externalPaginationState={{ pageSize, pageIndex }}
					onPaginationChange={({ pageSize: ps, pageIndex: pi }) => {
						if (ps !== pageSize) {
							setPageSize(ps);
							setPageIndex(0);
							savePageSize(ps);
						} else if (pi !== pageIndex) {
							setPageIndex(pi);
						}
					}}
					pageCount={pageCount}
					rowCount={countQuery.data}
					totalRowCount={unfilteredCountQuery.data}
					sortFieldMapping={serverSortFieldMapping}
					defaultSortField="name"
					onSortChange={onSortChange}
					onExportClick={handleExport}
					isExportLoading={exportMutation.isPending}
					filterPlaceholder={t("admin.assetTypes.table.search.placeholder")}
					filterValue={searchTerm}
					filterInputTestId="assettype-table-search-input"
					onDebouncedFilterChange={(s) => {
						setSearchTerm(s);
						setPageIndex(0);
						saveSearchTerm(s);
					}}
					enableTableActions
					filterLayout="bar"
					filterBarType="landing"
					filterBarClassName="pt-0"
					tableOptionsPortalTarget={tableOptionsPortalTarget}
					hasActiveFilters={searchTrimmed.length > 0}
					enableColumnReordering
					enableColumnResizing
					onRowClick={handleRowClick}
					getRowId={(row) => row.id}
					initialSortingState={tableSettings.sorting}
					initialColumnOrder={normalizeColumnOrder(tableSettings.columnOrder)}
					initialColumnVisibility={tableSettings.columnVisibility}
					settingsLoaded={settingsLoaded}
					onSortingStateChange={saveSorting}
					onColumnOrderChange={(order) => {
						saveColumnOrder(normalizeColumnOrder(order));
					}}
					onColumnVisibilityChange={saveColumnVisibility}
					initialSorting={DEFAULT_SORTING}
				/>
			</div>

			{!pageQuery.isLoading &&
			!pageQuery.isFetching &&
			!countQuery.isFetching &&
			(countQuery.data ?? 0) === 0 &&
			searchTrimmed.length === 0 ? (
				<div className="flex justify-center py-12 text-gray-500">
					{t("admin.assetTypes.table.empty.noAssetTypes")}
				</div>
			) : null}

			{!pageQuery.isLoading &&
			!pageQuery.isFetching &&
			!countQuery.isFetching &&
			(countQuery.data ?? 0) === 0 &&
			searchTrimmed.length > 0 ? (
				<TableSearchEmptyState titleKey="admin.assetTypes.table.empty.noSearchResults" />
			) : null}

			<DeleteAssetTypeModal
				isOpen={selectedAssetType !== null}
				setIsOpen={(open) => {
					if (!open) setSelectedAssetType(null);
				}}
				handleClose={() => setSelectedAssetType(null)}
				onDeleteSuccess={() => setSelectedAssetType(null)}
				assetTypeId={selectedAssetType?.id ?? ""}
				assetTypeName={selectedAssetType?.name ?? ""}
				organizationId={EMPTY_ORG_ID}
			/>
			<DuplicatingEntityTypeModal
				isOpen={duplicateAssetTypeMutation.isPending}
				anchorRef={tableAnchorRef}
				label={t("admin.assetTypes.duplicate.inProgress")}
				testId="assettype-duplicate-in-progress"
			/>
		</>
	);
}

function RouteComponent() {
	const { t } = useAppTranslation();
	const [isOpen, setIsOpen] = useState(false);
	const [tableOptionsHost, setTableOptionsHost] =
		useState<HTMLDivElement | null>(null);
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const isFullRoute = /\/configuration\/assetTypes\/[^/]+\/full/.test(pathname);
	const canCreateAssetType = useQuery(
		authQueryOptions.checkAction({
			name: "CreateAssetType",
		}),
	);

	if (isFullRoute) {
		return <Outlet />;
	}

	return (
		<>
			<PageLayout
				header={
					<PageHeader
						type="landing"
						title={t("admin.assetTypes.table.title")}
						description={t("admin.assetTypes.table.platformDescription")}
						actions={
							canCreateAssetType.data
								? [
										{
											label: t(
												"admin.assetTypes.table.actions.createAssetType",
											),
											color: "accent",
											onPress: () => setIsOpen(true),
											testId: "assettype-table-create-btn",
										},
									]
								: undefined
						}
						overflowControl={<div ref={setTableOptionsHost} />}
						overflowAriaLabel={t("common.tableActions.options")}
					/>
				}
			>
				<AssetTypesTable tableOptionsPortalTarget={tableOptionsHost} />
			</PageLayout>
			{canCreateAssetType.data ? (
				<CreateAssetTypeModal
					isOpen={isOpen}
					setIsOpen={setIsOpen}
					handleClose={() => {
						setIsOpen(false);
					}}
					mode="platform"
				/>
			) : null}
			<Outlet />
		</>
	);
}
